import AwesomeDebouncePromise from 'awesome-debounce-promise';
import classNames from 'classnames';
import { saveAs } from 'file-saver';
import { getOrganisationClients } from 'Hooks/Api/apiOrganisation';
import { OrganisationEntity } from 'Models/Entities';
import { accountTypeOptions, organisationCategoryOptions } from 'Models/Enums';
import { store } from 'Models/Store';
import moment from 'moment';
import { useState } from 'react';
import { buildBlob } from 'Util/CsvUtils';
import { formatAbn } from 'Util/StringUtils';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import ExportInvoicesModal from 'Views/Components/Clients/ExportInvoicesModal';
import EntityList from 'Views/Components/EntityList/EntityList';
import If from 'Views/Components/If/If';
import TablePagination from 'Views/Components/Pagination/TablePagination';
import RequestWrap, { GetRefreshKey } from 'Views/Components/RequestWrap/RequestWrap';
import { TextFieldSetter } from 'Views/Components/TextBox/TextFieldSetter';
import ProductSelectedBox from './ProductSelectedBox';

export const ORGANISATIONS_PAGE_LENGTH = 10;

// Method to fetch and return the OrganisationEntitys based on the page number and sorting
const fetchClientsDebounced = AwesomeDebouncePromise(async (
	search: string,
	sortColumn: string,
	sortDescending: boolean,
	page: number,
	setCount: (newCount: number) => void,
	noPagination?: boolean,
) => {
	return getOrganisationClients({
		search,
		sortColumn,
		sortDescending,
		page,
		setCount,
		noPagination,
	})
}, 300, {
	// We use leading, so that one-off changes like sorting columns apply immediately
	// Changing the search text will trigger on the first character, and then again after the final change
	leading: true,
});


const ClientList = () => {
	// For the search bar display
	const [search, setSearch] = useState('');
	const [sortColumn, setSortColumn] = useState('primaryBusinessEntity.name');
	const [sortDescending, setSortDescending] = useState(false);
	const [page, setPage] = useState(0);
	const [count, setCount] = useState(0);
	const [showExportInvoicesModal, setShowExportInvoicesModal] = useState(false);

	/**
	 * Callback for sorting changes
	 * @param header The name of the sorting column
	 */
	const onSortChange = (header: string) => {
		setSortDescending(sortColumn === header ? !sortDescending : false);
		setSortColumn(header);
	};

	// Method to fetch and return the OrganisationEntity based on the page number and sorting
	const fetchClientsForExport = async () => {
		const organisations: OrganisationEntity[] = await getOrganisationClients(
			{
				search,
				sortColumn,
				sortDescending,
				page,
				setCount,
				noPagination: true
			}
		);
		const result = [] as string[][];
		const columns = [
			{
				displayName: 'Status',
				value: (org: OrganisationEntity) => org.isActive.toString().toUpperCase(),
			},
			{
				displayName: 'OrganisationId',
				value: (org: OrganisationEntity) => org.id,
			},
			{
				displayName: 'OrganisationName',
				value: (org: OrganisationEntity) => org.primaryBusinessEntity.name,
			},
			{
				displayName: 'OrganisationABN',
				value: (org: OrganisationEntity) => org.primaryBusinessEntity.abn,
			},
			{
				displayName: 'OrganisationState',
				value: (org: OrganisationEntity) => org.primaryBusinessEntity.address?.state ?? "",
			},
			{
				displayName: 'OrganisationEmail',
				value: (org: OrganisationEntity) => org.primaryBusinessEntity.email,
			},
			{
				displayName: 'Account Type',
				value: (org: OrganisationEntity) => accountTypeOptions[org.accountType],
			},
			{
				displayName: 'OrganisationSize',
				value: (org: OrganisationEntity) => organisationCategoryOptions[org.organisationCategory],
			},
			{
				displayName: 'Users',
				value: (org: OrganisationEntity) => org.users.length.toString(),
			},
			{
				displayName: 'CustomerCount',
				value: (org: OrganisationEntity) => (org.customerCount ?? 0).toString(),
			},
			{
				displayName: 'Approve',
				value: (org: OrganisationEntity) => (org.approveEnabled ?? false).toString().toUpperCase(),
			},
			{
				displayName: 'Monitor',
				value: (org: OrganisationEntity) => (org.intelEnabled ?? false).toString().toUpperCase(),
			},
			{
				displayName: 'PPSR',
				value: (org: OrganisationEntity) => (org.ppsrEnabled ?? false).toString().toUpperCase(),
			},
			{
				displayName: 'ReferralPartner',
				value: (org: OrganisationEntity) => (org.isReferralPartner ?? false).toString().toUpperCase(),
			},
			{
				displayName: 'Referred by',
				value: ({ registration }: OrganisationEntity) => registration?.referringOrganisation
					?.primaryBusinessEntity?.name,
			},
			{
				displayName: 'Registered on',
				value: ({ created }: OrganisationEntity) => created
					? moment(created).format('DD/MM/YYYY') : '',
			},
			{
				displayName: 'Trial Expiry Date',
				value: ({ freeTrialExpiryDate }: OrganisationEntity) => freeTrialExpiryDate
					? moment(freeTrialExpiryDate).isBefore(moment()) ? `${moment(freeTrialExpiryDate).format('DD/MM/YYYY')} (expired)` : moment(freeTrialExpiryDate).format('DD/MM/YYYY') : '',
			},
			{
				displayName: 'Size',
				value: (org: OrganisationEntity) => organisationCategoryOptions[org.organisationCategory],
			},
			{
				displayName: 'Upfront payment',
				value: (org: OrganisationEntity) => org.enforceOnlinePayments?.toString() ?? "false",
			},
			{
				displayName: 'Monitored Customers',
				value: (org: OrganisationEntity) => (org.customerss.filter(customer => customer.monitoredStatus === 'MONITORED').length ?? 0).toString(),
			},
			{
				displayName: 'LegacyID',
				value: (org: OrganisationEntity) => org.legacyID?.toString(),
			},
		];
		const headers = columns.map(column => column.displayName);
		result.push(headers);
		organisations.forEach(client => {
			result.push(columns.map(column => column.value(client) ?? ''));
		});

		return result;
	};

	const handleExportCSV = async () => {
		const exportClients = await fetchClientsForExport();
		const blob = buildBlob(exportClients, ',', '"');
		saveAs(blob, `exported-clients-${moment().format('YYYY-MM-DD')}.csv`);
	};

	const clientListRefreshKey = GetRefreshKey(
		search,
		sortColumn,
		sortDescending,
		page,
	);

	return (
		<div className="client-list">
			<div className="search-container">
				<div className="searchbar search">
					<TextFieldSetter
						className={classNames('search-input search__collection')}
						value={search}
						setValue={setSearch}
						label=""
						onAfterChange={() => setPage(0)}
						labelVisible={false}
						placeholder="Search by Name or ABN"
					/>
				</div>
				<div className="actions">
					<Button
						className="export-invoices"
						display={Display.Outline}
						colors={Colors.Primary}
						onClick={() => setShowExportInvoicesModal(true)}
					>
						Export Invoices
					</Button>

					<Button
						className="export-button"
						colors={Colors.Alternate}
						display={Display.Outline}
						onClick={handleExportCSV}
					>
						Export CSV
					</Button>
				</div>
				<If condition={showExportInvoicesModal}>
					<ExportInvoicesModal
						onClose={() => setShowExportInvoicesModal(false)}
					/>
				</If>
			</div>
			<div className="collection-component">
				{/* Component to fetch and render the Client List */}
				<RequestWrap
					requestOptions={{
						suppressSubsequentLoadingState: false
					}}
					request={() => fetchClientsDebounced(
						search,
						sortColumn,
						sortDescending,
						page,
						setCount,
						false,
					)}
					refreshKey={clientListRefreshKey}
				>
					{(organisations: OrganisationEntity[]) => (
						<>
							<EntityList
								collection={organisations}
								columns={[
									{
										displayName: 'Name',
										columnName: 'primaryBusinessEntity.name',
										value: organisation => {
											const orgBusinessName = organisation.primaryBusinessEntity?.name;
											if (orgBusinessName == null) return <span className="not-provided">Not Provided</span>;
											return organisation.isActive === false
												? <span className="inactive">{orgBusinessName}</span>
												: orgBusinessName
										},
										sortable: true,
										sortClicked: () => {
											onSortChange('primaryBusinessEntity.name');
										},
										className: 'field-organisation-name',
									},
									{
										displayName: 'ABN',
										columnName: 'primaryBusinessEntity.abn',
										value: organisation => formatAbn(organisation.primaryBusinessEntity?.abn)
											?? <span className="not-provided">Not Provided</span>,
										sortable: true,
										sortClicked: () => {
											onSortChange('primaryBusinessEntity.abn');
										},
										className: 'field-abn',
									},
									{
										displayName: 'Products',
										columnName: 'products',
										value: organisation => (
											<ProductSelectedBox
												ppsrEnabled={organisation.ppsrEnabled}
												approveEnabled={organisation.approveEnabled}
												intelEnabled={organisation.intelEnabled}
												isReferrer={organisation.isReferralPartner}
											/>
										),
										sortable: false,
										className: 'field-products',
									},
									{
										displayName: 'Registered on',
										columnName: 'completedDate',
										value: organisation => organisation.created
											? moment(organisation.created).format('DD/MM/YYYY')
											: <span className="not-provided">Not Provided</span>,
										sortable: true,
										sortClicked: () => {
											onSortChange('created');
										},
										className: 'field-start-date',
									},
									{
										displayName: 'Size',
										columnName: 'organisationCategory',
										value: organisation => organisationCategoryOptions[organisation.organisationCategory],
										sortable: true,
										sortClicked: () => {
											onSortChange('organisationCategory');
										},
										className: 'field-size',
									},
									{
										displayName: 'Account Type',
										columnName: 'accountType',
										value: organisation => accountTypeOptions[organisation.accountType],
										sortable: true,
										sortClicked: () => {
											onSortChange('accountType');
										},
										className: 'field-account-type',
									},
									{
										displayName: 'Referred by',
										columnName: 'registration.referringOrganisation.primaryBusinessEntity.name',
										value: ({ registration }: OrganisationEntity) => registration?.referringOrganisation
											?.primaryBusinessEntity?.name ?? '-',
										sortable: true,
										sortClicked: () => {
											onSortChange('registration.referringOrganisation.primaryBusinessEntity.name');
										},
									},
									{
										displayName: 'Upfront payment',
										columnName: 'enforceOnlinePayments',
										value: organisation => organisation.enforceOnlinePayments?.toString() ?? "false",
										sortable: true,
										sortClicked: () => {
											onSortChange('enforceOnlinePayments');
										},
									},
									{
										displayName: "Monitored Customers",
										columnName: 'customerss.count',
										value: organisation => (organisation.customerss.filter(customer => customer.monitoredStatus === 'MONITORED').length ?? 0).toString(),
										sortable: true,
										sortClicked: () => {
											onSortChange('customerss.count');
										},
									}
								]}
								idColumn="id"
								sortColumn={sortColumn}
								sortDescending={sortDescending}
								onClickRow={organisation => {
									store.routerHistory.push(`/hub/clients/${organisation.id}`);
								}}
							/>
							<section className="collection__load">
								<TablePagination
									perPage={ORGANISATIONS_PAGE_LENGTH}
									pageNo={page}
									totalRecords={count}
									onPageChange={setPage}
								/>
							</section>
						</>
					)}
				</RequestWrap>
			</div>
		</div>
	);
};
export default ClientList;

